import type { PeerMetadata } from '@playcastdotio/Messaging';
import type { PeerType, PeerTypeHandler, SerializableMessage } from '../types';

/**
 * PeerTypeRegistry - Extensible handler registry for peer types.
 *
 * Each peer type registers behavior for join/leave/message events.
 * This drives which dedicated coordinator events fire and what
 * per-type setup/teardown logic runs.
 */
export class PeerTypeRegistry {
  private handlers = new Map<PeerType, PeerTypeHandler>();

  public register(peerType: PeerType, handler: PeerTypeHandler): void {
    if (this.handlers.has(peerType)) {
      console.warn(`[PeerTypeRegistry] Overwriting handler for type: ${peerType}`);
    }
    this.handlers.set(peerType, handler);
  }

  public unregister(peerType: PeerType): void {
    this.handlers.delete(peerType);
  }

  public hasHandler(peerType: PeerType): boolean {
    return this.handlers.has(peerType);
  }

  public handleConnected(peerType: PeerType, peerId: string, metadata: PeerMetadata): void {
    const handler = this.handlers.get(peerType);
    if (handler) {
      handler.onConnected(peerId, metadata);
    } else {
      console.warn(`[PeerTypeRegistry] No handler registered for type: ${peerType}`);
    }
  }

  public handleDisconnected(peerType: PeerType, peerId: string): void {
    const handler = this.handlers.get(peerType);
    if (handler) {
      handler.onDisconnected(peerId);
    }
  }

  public handleMessage(peerType: PeerType, peerId: string, message: SerializableMessage): void {
    const handler = this.handlers.get(peerType);
    if (handler) {
      handler.onMessage(peerId, message);
    }
  }

  public getRegisteredTypes(): PeerType[] {
    return Array.from(this.handlers.keys());
  }

  public destroy(): void {
    this.handlers.clear();
  }
}
